<script setup lang="ts">
import { useElementHover, useElementVisibility } from "@vueuse/core";
import { type EditorState, INSERT_PARAGRAPH_COMMAND, type LexicalEditor, type SerializedEditorState } from "lexical";
import { AutoLinkPlugin, LinkPlugin as LexicalLinkPlugin, ListPlugin, MarkdownShortcutPlugin } from "lexical-vue";
import { computed, nextTick, onUnmounted, provide, ref, watch } from "vue";
import { AVMedia } from "vue-audio-visual";
import { useRouter } from "vue-router";

import { backendOld } from "~/api";
import AttachmentChips from "~/components/chat/AttachmentChips.vue";
import ChatExtras from "~/components/chat/ChatExtras.vue";
import ChatModelSwitcher from "~/components/chat/ChatModelSwitcher.vue";
import { isAssistantTextMessage } from "~/components/chat/messageSectionUtils";
import AttachmentInputButton from "~/components/dumb/AttachmentInputButton.vue";
import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UpdateTime from "~/components/dumb/UpdateTime.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import { copyAndNotify } from "~/components/notifications/notification";
import TextEditorControls from "~/components/text/TextEditorControls.vue";
import {
  playSpeech,
  speechMessageDuid,
  speechPaused,
  speechStream,
  toggleSpeechPaused,
} from "~/components/textToSpeech/common";
import { ENTITY_KIND_URL, THROTTLE_MS } from "~/constants/app";
import { IS_STANDALONE_MEDIA } from "~/constants/injection";
import { DEFAULT_SKILL_PROMPT_BY_KIND } from "~/constants/skill";
import { colorsByTheme } from "~/constants/style";
import {
  DotsHorizontalIcon,
  DuidFieldIcon,
  MarkdownIcon,
  PauseIcon,
  PlayIcon,
  SendIcon,
  SkillIcon,
  StopIcon,
  UndoIcon,
} from "~/icons";
import { makeLinkToSkillSettingsRef } from "~/router/common";
import {
  ButtonSize,
  ButtonStyle,
  ChatMessageKind,
  CommandId,
  DropdownMenuItemKind,
  EditorMode,
  EntityName,
  IconSize,
  PageKind,
  PageLinkMode,
  Placement,
  SkillKind,
} from "~/shared/enums";
import type {
  AgentSchedule,
  Chat,
  DraftMessage,
  DropdownMenuSection,
  ExtraItem,
  Skill,
  TextMessage,
  TextSnapshotSource,
  UserTextMessage,
} from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { areLexicalStatesSame, isLexicalStateEmpty, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { getOrdersBetween } from "~/utils/orderManager";
import { ThrottleManager } from "~/utils/throttleManager";
import { when } from "~/utils/wait";

import { EDITOR_MIN_HEIGHT_PX, URL_MATCHERS } from "./const";
import AttachmentPlugin from "./plugins/AttachmentPlugin.vue";
import ChatBackspacePlugin from "./plugins/ChatBackspacePlugin.vue";
import ChatHistoryPlugin from "./plugins/ChatHistoryPlugin.vue";
import CheckListPlugin from "./plugins/CheckListPlugin.vue";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin.vue";
import CodeSelectorPlugin from "./plugins/CodeSelectorPlugin.vue";
import ColorPlugin from "./plugins/ColorPlugin.vue";
import EnterPlugin from "./plugins/EnterPlugin.vue";
import FloatingToolbar from "./plugins/FloatingToolbar.vue";
import LinkPlugin from "./plugins/LinkPlugin.vue";
import ManageTextFormatPlugin from "./plugins/ManageTextFormatPlugin.vue";
import OnChangePlugin from "./plugins/OnChangePlugin.vue";
import PasteMarkdownPlugin from "./plugins/PasteMarkdownPlugin.vue";
import PlaceholderPlugin from "./plugins/PlaceholderPlugin.vue";
import QuoteSectionPlugin from "./plugins/QuoteSectionPlugin.vue";
import ShortcutsPlugin from "./plugins/ShortcutsPlugin.vue";
import TablePlugin from "./plugins/TablePlugin.vue";
import TabPlugin from "./plugins/TabPlugin.vue";
import TextInsertPlugin from "./plugins/TextInsertPlugin.vue";
import AiChatTypeaheadPlugin from "./plugins/typeahead/AiChatTypeaheadPlugin.vue";
import TextEditor from "./TextEditor.vue";
import { getCopyItems } from "./copyItems";
import { convertToMarkdown, TRANSFORMERS } from "./transformers";
import {
  convertMarkdownToSerializedNodes,
  PARTIAL_DART_EDITOR_NODES,
  trimSerializedLexicalState,
  useStreamText,
} from "./utils";

const BUTTON_STYLE = "flex items-center rounded p-0.5 text-lt hover:bg-lt focus-ring-std";

const props = defineProps<{
  // Chat mode props
  chat?: Chat;
  message?: TextMessage | DraftMessage;
  isDraft?: boolean;
  // Skill mode props
  skill?: Skill;
  // Agent mode props
  agentDuid?: string;
  agentSchedule?: AgentSchedule;
  // Version history (skill or agent)
  textSnapshotSource?: TextSnapshotSource;
  // Common props
  editorClasses?: string;
  placeholder?: string;
  baseText?: boolean;
  isSendSecondaryButton?: boolean;
  working?: boolean;
  hideAttachmentButton?: boolean;
  hideContextButton?: boolean;
  hideModelSwitcher?: boolean;
  modelSwitcherPlacement?: Placement;
  fitContent?: boolean;
  maxHeightPx?: number;
  inlineButtons?: boolean;
  noTopPadding?: boolean;
  toolbarTeleportKey: string;
}>();

const emit = defineEmits<{
  send: [message: UserTextMessage];
  stop: [];
  streamUpdated: [];
  updateExtras: [extras: ExtraItem[]];
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const PAGE_KIND_TO_ENTITY_NAME_MAP = new Map([
  [PageKind.DOC, EntityName.DOC],
  [PageKind.DARTBOARD, EntityName.DARTBOARD],
  [PageKind.VIEW, EntityName.VIEW],
  [PageKind.FOLDER, EntityName.FOLDER],
]);

const getUiContextExtras = (): ExtraItem[] => {
  const extras: ExtraItem[] = [];
  const { currentPage } = appStore;
  const pageEntityName = currentPage ? PAGE_KIND_TO_ENTITY_NAME_MAP.get(currentPage.pageKind) : undefined;
  if (pageEntityName) {
    extras.push({ kind: pageEntityName, duid: currentPage?.duid });
  }
  if (appStore.selectedTaskDuids.size > 0) {
    extras.push(...[...appStore.selectedTaskDuids].map((duid) => ({ kind: EntityName.TASK, duid })));
  }
  if (appStore.docOpenInFullscreen) {
    extras.push({ kind: EntityName.DOC, duid: appStore.docOpenInFullscreen.duid });
  }
  return extras;
};

const chatExtras = ref<InstanceType<typeof ChatExtras> | null>(null);
const skillAttachmentChips = ref<InstanceType<typeof AttachmentChips> | null>(null);
const attachmentInput = ref<InstanceType<typeof AttachmentInputButton> | null>(null);
const textEditor = ref<InstanceType<typeof TextEditor> | null>(null);
const textInsertPlugin = ref<InstanceType<typeof TextInsertPlugin> | null>(null);
const chatHistoryPlugin = ref<InstanceType<typeof ChatHistoryPlugin> | null>(null);
const typeaheadPlugin = ref<InstanceType<typeof AiChatTypeaheadPlugin> | null>(null);
const textEditorControls = ref<InstanceType<typeof TextEditorControls> | null>(null);
const revertSkillDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const menuWrapper = ref<HTMLElement | null>(null);

const getChatAndDraft = (chatDuid: string | undefined) => {
  if (!chatDuid) {
    return undefined;
  }

  const chat = dataStore.getChatByDuidIfLoaded(chatDuid);
  const draft = dataStore.getDraftMessageByChatDuid(chatDuid);
  if (!chat || !draft) {
    return undefined;
  }
  return { chat, draft };
};

const isChat = computed(() => props.chat !== undefined);
const isAgent = computed(() => props.agentDuid !== undefined);
const isAgentSchedule = computed(() => props.agentSchedule !== undefined);
const canUseAttachments = computed(() => !isAgent.value && !isAgentSchedule.value);
const isChatDraftEditor = computed(() => isChat.value && !!props.chat && !!props.isDraft);
const activeDraftMessage = computed(() =>
  props.chat
    ? (dataStore.getDraftMessageByChatDuid(props.chat.duid) ??
      (props.isDraft && props.message?.kind === ChatMessageKind.DRAFT ? props.message : undefined))
    : props.isDraft && props.message?.kind === ChatMessageKind.DRAFT
      ? props.message
      : undefined
);
provide(IS_STANDALONE_MEDIA, isChat.value);

const getAgentPrompt = () => {
  if (!props.agentDuid) {
    return undefined;
  }
  const agent = dataStore.getUserByDuid(props.agentDuid);
  return agent?.agent?.prompt;
};

const initialState = ref(
  (isChat.value
    ? props.message?.content
    : isAgent.value
      ? getAgentPrompt()
      : isAgentSchedule.value
        ? props.agentSchedule?.prompt
        : props.skill?.prompt) ?? makeEmptyLexicalState()
);
const content = ref(initialState.value);
const isEmpty = ref(isLexicalStateEmpty(content.value));
const isEmptyForPlaceholder = ref(isLexicalStateEmpty(content.value, undefined, true));

const isTrimmedContentEmpty = () =>
  content.value.root.children.length === 0 || isLexicalStateEmpty(trimSerializedLexicalState(content.value));
const isSendMessageDisabled = ref(isTrimmedContentEmpty());

const namespace = computed(() =>
  isChat.value
    ? `message-${props.isDraft}-${props.message?.duid}`
    : isAgent.value
      ? `agent-${props.agentDuid}`
      : isAgentSchedule.value
        ? `agent-schedule-${props.agentSchedule?.duid}`
        : `skill-${props.skill?.duid}`
);

const isEditable = computed(() => (isChat.value ? props.isDraft : true));

const hasFocus = computed(() => textEditor.value?.hasFocus ?? false);
const isReady = computed(() => textEditor.value?.isReady ?? false);
const isMultiline = computed(() => props.inlineButtons && !(textEditor.value?.buttonsInline ?? true));
const {
  streamText,
  isStreaming,
  stop: stopTextStream,
} = useStreamText((text) => {
  textEditor.value?.updateFromMarkdown(text);
  emit("streamUpdated");
});
const isAttachmentUploading = computed(
  () => chatExtras.value?.isAttachmentUploading ?? skillAttachmentChips.value?.isAttachmentUploading ?? false
);
const shouldShowSkillAttachmentSpacing = computed(
  () => (props.skill?.attachmentDuids.length ?? 0) > 0 || isAttachmentUploading.value
);
const messageModel = computed(() =>
  props.isDraft ? props.chat?.model : props.message && "model" in props.message ? props.message.model : undefined
);

const maxHeightPxNorm = computed(() => props.maxHeightPx ?? (isChat.value && isEditable.value ? 300 : undefined));

const placeholder = computed(() => {
  if (props.placeholder !== undefined) {
    return props.placeholder;
  }
  if (isChat.value) {
    return props.isDraft && isEmptyForPlaceholder.value ? "Plan, search, and create anything" : "";
  }
  if (isAgent.value) {
    return "Describe what this agent should do";
  }
  if (isAgentSchedule.value) {
    return "Describe what this agent should do on this schedule";
  }
  return "Describe what this skill should do";
});

const isRecording = computed(() => textEditorControls.value?.working);
const convertMarkdownToSerializedEditorState = (markdown: string): SerializedEditorState => {
  const emptyState = makeEmptyLexicalState();
  return {
    ...emptyState,
    root: {
      ...emptyState.root,
      children: convertMarkdownToSerializedNodes(markdown),
    },
  };
};

const defaultSkillPrompt = computed(() => {
  if (!props.skill || props.skill.kind === SkillKind.CUSTOM) {
    return undefined;
  }

  const markdown = DEFAULT_SKILL_PROMPT_BY_KIND[props.skill.kind];
  if (!markdown) {
    return undefined;
  }

  return trimSerializedLexicalState(convertMarkdownToSerializedEditorState(markdown));
});
const shouldShowRevertDefaultSkillPrompt = computed(() => {
  if (!props.skill || !defaultSkillPrompt.value || isChat.value) {
    return false;
  }
  return !areLexicalStatesSame(trimSerializedLexicalState(content.value), defaultSkillPrompt.value);
});
const skillResetSections = computed<DropdownMenuSection[]>(() =>
  shouldShowRevertDefaultSkillPrompt.value
    ? [
        {
          title: "Skill",
          items: [
            {
              title: "Revert skill to default",
              kind: DropdownMenuItemKind.BUTTON,
              icon: UndoIcon,
              onClick: () => revertSkillDialog.value?.openModal(),
            },
          ],
        },
      ]
    : []
);

const isSpeechMessage = computed(() => !!props.message && speechMessageDuid.value === props.message.duid);
const speechButtonLabel = computed(() => {
  if (!isSpeechMessage.value) {
    return "Read aloud";
  }
  return speechPaused.value ? "Resume" : "Pause";
});
const visualizerLineColor = computed(() => colorsByTheme[pageStore.theme].textVlt);
const isMessageHovered = useElementHover(() => textEditor.value?.editorElem, { triggerOnRemoval: true });
const isMenuHovered = useElementHover(menuWrapper, { triggerOnRemoval: true });
const isMessageVisible = useElementVisibility(() => textEditor.value?.editorElem);
const isMenuShown = computed(
  () => (isSpeechMessage.value && isMessageVisible.value) || isMessageHovered.value || isMenuHovered.value
);

const toggleSpeech = () => {
  if (!props.message) {
    return;
  }
  if (isSpeechMessage.value) {
    toggleSpeechPaused();
    return;
  }
  playSpeech(props.message.duid, props.message.chatDuid);
};

const copyMessageAsMarkdown = () => {
  if (!props.message?.content) {
    return;
  }
  copyAndNotify("Message", convertToMarkdown(props.message.content));
};

const saveAsSkill = async () => {
  if (!props.message?.content || isAssistantTextMessage(props.message)) {
    return;
  }

  const skills = dataStore.skillList({ includeHidden: true });
  const order = getOrdersBetween(undefined, skills[0]?.order)[0];

  const skill = await dataStore.createSkill("Skill", order, {
    prompt: props.message.content,
    extras: props.message.extras ?? [],
    attachmentDuids: props.message.attachmentDuids ?? [],
  });

  if (!skill) {
    return;
  }

  const route = makeLinkToSkillSettingsRef(skill.duid).value;
  await router.push({ ...route, query: { ...route.query, focus: "title" } });
};

const messageMenuSections = computed((): DropdownMenuSection[] => {
  const sections: DropdownMenuSection[] = [];
  const isUserMessage = !!props.message && !isAssistantTextMessage(props.message);

  if (isUserMessage) {
    sections.push({
      title: "Skill",
      items: [
        {
          title: "Save as skill",
          kind: DropdownMenuItemKind.BUTTON,
          icon: SkillIcon,
          onClick: saveAsSkill,
        },
      ],
    });
  }

  sections.push({
    title: "Actions",
    items: [
      ...getCopyItems("Message", props.message?.content),
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => {
          if (!props.message?.duid) {
            return;
          }
          copyAndNotify("Message ID", props.message.duid);
        },
      },
    ],
  });

  return sections;
});

// Save managers for all modes
const saveManager = new ThrottleManager(
  (
    value: SerializedEditorState,
    chatDuid: string | undefined,
    skillDuid: string | undefined,
    agentDuid: string | undefined,
    agentScheduleDuid: string | undefined
  ) => {
    if (agentScheduleDuid) {
      if (isLexicalStateEmpty(value)) {
        return;
      }
      dataStore.updateAgentSchedule({ duid: agentScheduleDuid, prompt: value });
      return;
    }

    if (agentDuid) {
      const agent = dataStore.getUserByDuid(agentDuid);
      if (agent?.agent) {
        dataStore.updateAgent({ duid: agentDuid, agent: { ...agent.agent, prompt: value } });
      }
      return;
    }

    if (skillDuid) {
      dataStore.updateSkill({ duid: skillDuid, prompt: value });
      return;
    }

    if (!chatDuid || !props.isDraft) {
      return;
    }

    const currentChatAndDraft = getChatAndDraft(chatDuid);
    if (!currentChatAndDraft) {
      return;
    }

    dataStore.updateChatDraftMessage(currentChatAndDraft.chat.duid, {
      ...currentChatAndDraft.draft,
      content: value,
    });
  },
  THROTTLE_MS
);

const onFocusedChange = (newEditorState: EditorState) => {
  if (isChat.value && !props.isDraft) {
    return;
  }

  const value = newEditorState.toJSON();
  content.value = value;

  if (isAgent.value && props.agentDuid) {
    const agent = dataStore.getUserByDuid(props.agentDuid);
    if (agent?.agent) {
      dataStore.updateAgent({ duid: props.agentDuid, agent: { ...agent.agent, prompt: value } }, { noBackend: true });
    }
  }

  saveManager.run(value, props.chat?.duid, props.skill?.duid, props.agentDuid, props.agentSchedule?.duid);
};

const onUnfocusedChange = (newEditorState: EditorState) => {
  const value = newEditorState.toJSON();
  content.value = value;
  isEmpty.value = isLexicalStateEmpty(content.value);
  isEmptyForPlaceholder.value = isLexicalStateEmpty(content.value, undefined, true);
  isSendMessageDisabled.value = isTrimmedContentEmpty();
};

const trimOnBlur = () => {
  if (isChat.value) {
    return;
  }
  const trimmedState = trimSerializedLexicalState(content.value);

  if (isAgent.value && props.agentDuid) {
    const agent = dataStore.getUserByDuid(props.agentDuid);
    if (!agent?.agent || areLexicalStatesSame(trimmedState, agent.agent.prompt)) {
      return;
    }
    saveManager.cancel();
    dataStore.updateAgent({
      duid: props.agentDuid,
      agent: { ...agent.agent, prompt: trimmedState },
    });
    return;
  }

  if (props.agentSchedule) {
    const schedule = dataStore.getAgentScheduleByDuid(props.agentSchedule.duid);
    if (!schedule || areLexicalStatesSame(trimmedState, schedule.prompt) || isLexicalStateEmpty(trimmedState)) {
      return;
    }
    saveManager.cancel();
    dataStore.updateAgentSchedule({ duid: schedule.duid, prompt: trimmedState });
    return;
  }

  if (!props.skill) {
    return;
  }
  const skill = dataStore.getSkillByDuid(props.skill.duid);
  if (!skill) {
    return;
  }
  if (areLexicalStatesSame(trimmedState, skill.prompt)) {
    return;
  }
  saveManager.cancel();
  dataStore.updateSkill({ duid: skill.duid, prompt: trimmedState });
};

const focus = async () => {
  await when(isReady);
  textEditor.value?.focus();
};

// Chat-specific functions
const stopStreaming = async () => {
  if (!props.chat || !props.working) {
    return;
  }
  emit("stop");
  await backendOld.chat.stopStreaming(props.chat.duid);
};

const sendMessage = async () => {
  if (
    isSendMessageDisabled.value ||
    !props.chat ||
    !props.isDraft ||
    isRecording.value ||
    isAttachmentUploading.value
  ) {
    return;
  }

  if (props.working) {
    await stopStreaming();
  }

  const chat = dataStore.getChatByDuidIfLoaded(props.chat.duid) ?? props.chat;
  if (!chat) {
    return;
  }

  const trimmedState = trimSerializedLexicalState(content.value);
  if (isLexicalStateEmpty(trimmedState)) {
    return;
  }

  await chatExtras.value?.removeIncompatibleAttachments();
  const attachmentDuids = activeDraftMessage.value?.attachmentDuids ?? [];
  const extraKey = (e: ExtraItem) => (e.kind === ENTITY_KIND_URL ? e.url : e.duid);
  const allExtras = [
    ...(activeDraftMessage.value?.extras ?? []),
    ...(chat.extrasAreUserManaged ? [] : getUiContextExtras()),
  ];
  const extras = [...new Map(allExtras.map((e) => [extraKey(e), e])).values()];

  saveManager.cancel();
  chatHistoryPlugin.value?.resetDraftSnapshot();
  emit("send", {
    duid: makeDuid(),
    chatDuid: props.chat.duid,
    content: trimmedState,
    extras,
    kind: ChatMessageKind.USER_TEXT,
    authorDuid: activeDraftMessage.value?.authorDuid ?? userStore.duid,
    createdAt: new Date().toISOString(),
    attachmentDuids,
    webEnabled: props.chat.webEnabled,
  });

  textEditor.value?.setEditorState(makeEmptyLexicalState());
  nextTick(focus);
};

const onEnter = (event: KeyboardEvent, editor: LexicalEditor) => {
  if (event.shiftKey) {
    editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
    return;
  }
  if (props.working) {
    return;
  }
  sendMessage();
};

const saveDraftUnthrottled = (value: SerializedEditorState) => {
  saveManager.cancel();

  if (!props.chat?.duid) {
    return;
  }
  const currentChatAndDraft = getChatAndDraft(props.chat.duid);
  if (!currentChatAndDraft) {
    return;
  }

  dataStore.updateChatDraftMessage(currentChatAndDraft.chat.duid, {
    ...currentChatAndDraft.draft,
    content: value,
  });
};

const onSpeechText = (text: string) => {
  textInsertPlugin.value?.insert(text);
};

const revertSkillPromptToDefault = () => {
  if (!props.skill || !defaultSkillPrompt.value) {
    return;
  }

  saveManager.cancel();
  dataStore.updateSkill({ duid: props.skill.duid, prompt: defaultSkillPrompt.value });
  textEditor.value?.setEditorState(defaultSkillPrompt.value);
};

const updateExtras = (extras: ExtraItem[]) => {
  emit("updateExtras", extras);
};

const addAttachments = (files: File[]) => {
  if (!canUseAttachments.value) {
    return;
  }

  if (isChat.value) {
    chatExtras.value?.addAttachments(files);
    return;
  }
  skillAttachmentChips.value?.addAttachments(files);
};

const addSkillAttachmentsToChat = (attachmentDuids: string[]) => {
  if (!isChat.value) {
    return;
  }

  const currentChatAndDraft = getChatAndDraft(props.chat?.duid);
  if (!currentChatAndDraft) {
    return;
  }
  dataStore.updateChatDraftMessage(currentChatAndDraft.chat.duid, {
    ...currentChatAndDraft.draft,
    attachmentDuids: [...new Set([...currentChatAndDraft.draft.attachmentDuids, ...attachmentDuids])],
  });
};

const openAttachmentInput = () => {
  if (!canUseAttachments.value) {
    return;
  }
  attachmentInput.value?.open();
};

// Skill-specific functions
const insertSkill = (skill: Skill) => {
  typeaheadPlugin.value?.insertSkillLinkNode(skill.duid);
  if (skill.attachmentDuids.length > 0) {
    addSkillAttachmentsToChat(skill.attachmentDuids);
  }
  focus();
};

const insertSerializedState = (state: SerializedEditorState) => {
  typeaheadPlugin.value?.insertSerializedState(state);
  focus();
};

// Watchers
watch(
  () =>
    isChat.value
      ? props.chat?.duid
      : isAgent.value
        ? props.agentDuid
        : isAgentSchedule.value
          ? props.agentSchedule?.duid
          : props.skill?.duid,
  (duid) => {
    if (!duid) {
      return;
    }
    const lexicalContent = isChat.value
      ? (dataStore.getDraftMessageByChatDuid(duid)?.content ??
        (props.message?.kind === ChatMessageKind.DRAFT ? props.message.content : undefined))
      : isAgent.value
        ? dataStore.getUserByDuid(duid)?.agent?.prompt
        : isAgentSchedule.value
          ? dataStore.getAgentScheduleByDuid(duid)?.prompt
          : dataStore.getSkillByDuid(duid)?.prompt;

    if (!lexicalContent || (isChat.value && !props.isDraft)) {
      return;
    }

    saveManager.finish();
    textEditor.value?.setEditorState(lexicalContent);
  }
);

watch(
  () => props.skill?.prompt,
  (newText) => {
    if (!newText || areLexicalStatesSame(newText, content.value)) {
      return;
    }

    saveManager.finish();
    textEditor.value?.setEditorState(newText);
  }
);

watch(
  () => (props.agentDuid ? dataStore.getUserByDuid(props.agentDuid)?.agent?.prompt : undefined),
  (newText) => {
    if (!newText || hasFocus.value || areLexicalStatesSame(newText, content.value)) {
      return;
    }

    saveManager.finish();
    textEditor.value?.setEditorState(newText);
  }
);

watch(
  () => props.agentSchedule?.prompt,
  (newText) => {
    if (!newText || hasFocus.value || areLexicalStatesSame(newText, content.value)) {
      return;
    }

    saveManager.finish();
    textEditor.value?.setEditorState(newText);
  }
);

onUnmounted(() => {
  stopTextStream();
  if (defaultSkillPrompt.value && isTrimmedContentEmpty()) {
    revertSkillPromptToDefault();
    return;
  }
  // saveDraftUnthrottled handles the chat case
  if (!isChat.value) {
    saveManager.finish();
  }
});

const restoreState = (state: SerializedEditorState) => {
  saveManager.cancel();
  textEditor.value?.setEditorState(state);
  content.value = state;
  if (isAgent.value && props.agentDuid) {
    const agent = dataStore.getUserByDuid(props.agentDuid);
    if (agent?.agent) {
      dataStore.updateAgent({ duid: props.agentDuid, agent: { ...agent.agent, prompt: state } });
    }
    return;
  }
  if (props.agentSchedule) {
    dataStore.updateAgentSchedule({ duid: props.agentSchedule.duid, prompt: state });
    return;
  }
  if (props.skill) {
    dataStore.updateSkill({ duid: props.skill.duid, prompt: state });
  }
};

defineExpose({
  editorElem: computed(() => textEditor.value?.editorElem),
  content,
  isMultiline,
  focus,
  isReady,
  isStreaming,
  streamText,
  stopTextStream,
  insertSerializedState,
  insertSkill,
  revertSkillPromptToDefault,
  sendMessage,
  confirmClearActiveRecording: () => textEditorControls.value?.confirmClearActiveRecording(),
  addAttachments,
  save: () => saveManager.finish(),
});
</script>

<template>
  <GlobalPopper
    :type="PopperType.MENU"
    :disabled="!isChat || !message || isStreaming || isDraft"
    no-auto-focus
    placement="bottom-end"
    :distance="-6"
    :skidding="-8"
    :delay="0"
    :shown="isMenuShown"
    :triggers="[]"
    :popper-triggers="[]"
    :auto-hide="false"
    no-wrapper
    class="overflow-hidden"
    :class="isChat && ((message && isAssistantTextMessage(message)) || isStreaming || isDraft ? 'w-full' : 'ml-8')">
    <template #default="{ triggerRef }">
      <TextEditor
        :ref="
          (e) => {
            const element = e as InstanceType<typeof TextEditor> | null;
            textEditor = element;
            triggerRef(element?.editorElem ?? null);
          }
        "
        :grow="fitContent"
        :namespace="namespace"
        :nodes="PARTIAL_DART_EDITOR_NODES"
        :initial-state="initialState"
        :placeholder="placeholder"
        :disabled="!isEditable"
        :is-draft="isDraft"
        extra-rounded
        :top-margin-px="isChat ? 0 : shouldShowSkillAttachmentSpacing ? 2 : 6"
        :bottom-margin-px="isChat ? undefined : 7"
        :left-margin-px="isEditable ? 12 : 0"
        :right-margin-px="isEditable ? 12 : 0"
        :inline-buttons="inlineButtons ? isEmpty : undefined"
        overlay-scrollbar
        :max-height-px="maxHeightPxNorm"
        :min-height-px="
          isChat ? (isEditable && !inlineButtons ? EDITOR_MIN_HEIGHT_PX : undefined) : isAgentSchedule ? 192 : 200
        "
        :always-borderless="isChat && !isEditable"
        :editor-classes="editorClasses"
        :base-text="baseText"
        @blur="trimOnBlur">
        <template #before>
          <ChatExtras
            v-if="isChat && message && !isAssistantTextMessage(message)"
            ref="chatExtras"
            :chat="chat!"
            :extras="message.extras"
            :is-draft="Boolean(isDraft)"
            :message-model="messageModel"
            :no-top-padding="noTopPadding"
            :attachment-duids="message.attachmentDuids"
            @update-extras="updateExtras" />
          <div
            v-else-if="!isChat && skill"
            class="mb-0.5 flex w-full min-w-0 flex-wrap items-center gap-1 overflow-hidden"
            :class="shouldShowSkillAttachmentSpacing ? 'px-3 pt-2.5' : ''">
            <AttachmentChips
              ref="skillAttachmentChips"
              :skill-duid="skill.duid"
              :attachment-duids="skill.attachmentDuids"
              :mode="PageLinkMode.CHAT_ENABLED" />
          </div>
          <AttachmentInputButton
            v-if="canUseAttachments && !hideAttachmentButton"
            ref="attachmentInput"
            class="hidden"
            :editor-mode="EditorMode.DOC"
            @input="addAttachments" />
        </template>

        <template #default>
          <!-- lexical-vue plugins -->
          <AutoLinkPlugin :matchers="URL_MATCHERS" />
          <LexicalLinkPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin :transformers="TRANSFORMERS" />
          <!-- custom plugins -->
          <AiChatTypeaheadPlugin
            ref="typeaheadPlugin"
            :is-chat="isChat"
            :hide-attachment-option="hideAttachmentButton"
            @open-attachment-input="openAttachmentInput"
            @insert-attachment-duids="addSkillAttachmentsToChat" />
          <AttachmentPlugin v-if="isEditable && canUseAttachments" emit-only @add-attachments="addAttachments" />
          <ChatHistoryPlugin
            v-if="isChatDraftEditor && chat"
            ref="chatHistoryPlugin"
            :chat-duid="chat.duid"
            @save-unthrottled="saveDraftUnthrottled" />
          <ChatBackspacePlugin :chat-duid="chat?.duid" :skill-duid="skill?.duid" :is-empty="isEmpty" />
          <CheckListPlugin />
          <CodeHighlightPlugin />
          <CodeSelectorPlugin :editable="isEditable" />
          <ColorPlugin />
          <EnterPlugin v-if="isChat && !pageStore.isMobile" @enter="onEnter" />
          <FloatingToolbar v-if="isEditable" :teleport-key="toolbarTeleportKey" hide-ai-action-button />
          <LinkPlugin />
          <ManageTextFormatPlugin />
          <OnChangePlugin :has-focus="hasFocus" @change="onFocusedChange" @unfocused-change="onUnfocusedChange" />
          <PasteMarkdownPlugin />
          <PlaceholderPlugin v-if="isEditable" no-ai />
          <QuoteSectionPlugin />
          <ShortcutsPlugin :text-editor-controls="textEditorControls" />
          <TablePlugin />
          <TabPlugin />
          <TextInsertPlugin ref="textInsertPlugin" />
        </template>
        <template #after>
          <div
            v-if="isChatDraftEditor || !isChat"
            class="flex w-full cursor-text"
            :class="
              isChatDraftEditor
                ? [
                    'items-center',
                    hideContextButton ? 'justify-end' : 'justify-between',
                    inlineButtons ? (isMultiline ? 'pb-0.5' : '') : 'py-1 pb-1.5',
                  ]
                : 'items-center justify-end pt-2 pr-[7px] pb-[7px] pl-3'
            ">
            <ChatExtras
              v-if="isChatDraftEditor && chat && message && !isAssistantTextMessage(message)"
              is-menu
              :chat="chat"
              :extras="message.extras"
              :is-draft="Boolean(isDraft)"
              :attachment-duids="message.attachmentDuids"
              :hide-attachment-option="hideAttachmentButton"
              :hide-context-button="hideContextButton"
              @update-extras="updateExtras"
              @insert-skill="insertSkill"
              @open-attachment-input="openAttachmentInput" />
            <div class="flex min-w-0 items-center gap-1" :class="isChatDraftEditor && 'pr-1.5'">
              <div v-if="shouldShowRevertDefaultSkillPrompt" class="hidden">
                <ConfirmationDialog
                  ref="revertSkillDialog"
                  title="Revert skill text to default"
                  description="Reverting the skill text will replace your current edits with the default text. This can't be undone. Are you sure you want to proceed?"
                  confirm-text="Revert"
                  cancel-text="Keep"
                  :icon="UndoIcon"
                  @confirm="revertSkillPromptToDefault" />
              </div>
              <ChatModelSwitcher
                v-if="isChatDraftEditor && chat && !hideModelSwitcher"
                :chat="chat"
                :placement="modelSwitcherPlacement" />
              <TextEditorControls
                ref="textEditorControls"
                is-contrast
                :show-attachment="canUseAttachments && !isChat && !hideAttachmentButton"
                :text-history-source="textSnapshotSource"
                :get-content="() => content"
                :sections="skillResetSections"
                :width-pixels="skillResetSections.length > 0 ? 200 : undefined"
                @restore="restoreState"
                @result="onSpeechText"
                @attachment="addAttachments" />
              <Tooltip
                v-if="!isRecording && (working || isChatDraftEditor)"
                :command-id="isAttachmentUploading ? undefined : working ? CommandId.STOP_CHAT : CommandId.SAVE_CHAT"
                :text="isAttachmentUploading ? 'Uploading attachments' : undefined">
                <Button
                  v-if="working"
                  :btn-style="ButtonStyle.SECONDARY"
                  :icon="StopIcon"
                  :icon-args="{ class: 'text-vlt' }"
                  :size="ButtonSize.SMALL"
                  :icon-size="IconSize.M"
                  borderless
                  is-contrast
                  a11y-label="Stop"
                  class="!p-0.5"
                  @click="stopStreaming" />
                <Button
                  v-else-if="isChatDraftEditor"
                  :btn-style="isSendSecondaryButton ? ButtonStyle.SECONDARY : ButtonStyle.RECOMMENDATION"
                  :icon="SendIcon"
                  :size="ButtonSize.SMALL"
                  :icon-size="IconSize.M"
                  is-contrast
                  a11y-label="Send chat message"
                  class="!p-0.5"
                  :disabled="isSendMessageDisabled || isAttachmentUploading"
                  @click="sendMessage" />
              </Tooltip>
            </div>
          </div>
        </template>
      </TextEditor>
    </template>
    <template v-if="message" #popper>
      <div ref="menuWrapper" class="flex items-center gap-0.5 rounded-sm border border-md bg-std px-0.5">
        <div class="cursor-default border-r border-md pr-1.5 pl-1">
          <UpdateTime :at="message.createdAt" />
        </div>
        <Tooltip v-if="isAssistantTextMessage(message)" :text="speechButtonLabel">
          <button
            type="button"
            class="group/speech-button"
            :class="BUTTON_STYLE"
            :aria-label="speechButtonLabel"
            @click="toggleSpeech">
            <LoadingSpinner v-if="isSpeechMessage && !speechStream" x-small immediate />
            <template v-else-if="isSpeechMessage && speechStream">
              <AVMedia
                :media="speechStream"
                type="frequ"
                :canv-width="48"
                :canv-height="48"
                :line-color="visualizerLineColor"
                :frequ-lnum="5"
                frequ-line-cap
                :line-width="6"
                :fft-size="512"
                :class="speechPaused ? 'hidden' : 'icon-sm group-hover/speech-button:hidden'" />
              <PlayIcon v-if="speechPaused" class="icon-sm" />
              <PauseIcon v-else class="hidden icon-sm group-hover/speech-button:block" />
            </template>
            <PlayIcon v-else class="icon-sm" />
          </button>
        </Tooltip>
        <Tooltip v-if="isAssistantTextMessage(message)" text="Copy as markdown">
          <button type="button" :class="BUTTON_STYLE" aria-label="Copy as markdown" @click="copyMessageAsMarkdown">
            <MarkdownIcon class="icon-sm" />
          </button>
        </Tooltip>
        <Tooltip v-else text="Save as skill">
          <button type="button" :class="BUTTON_STYLE" aria-label="Save as skill" @click="saveAsSkill">
            <SkillIcon class="icon-sm" />
          </button>
        </Tooltip>
        <DropdownMenu
          :sections="messageMenuSections"
          :placement="Placement.BOTTOM_RIGHT"
          :width-pixels="192"
          :distance="2">
          <Tooltip text="Options">
            <button type="button" :class="BUTTON_STYLE">
              <span class="sr-only">Options</span>
              <DotsHorizontalIcon class="icon-sm" />
            </button>
          </Tooltip>
        </DropdownMenu>
      </div>
    </template>
  </GlobalPopper>
</template>
